/**
 * `v3.intake.*` — run intake for members, scoped to the caller's customer: a person's proposals to run a
 * flexible loop — made and revised by them or their coding agent, approved only by their own explicit
 * decision, withdrawn, and started as a normal v3 run that records the revision it started from.
 *
 * Handlers: `v3-intake.router.ts`.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { RunStartResult } from "@fm/v3";
import {
  IntakeStartInput,
  ProposalApproveInput,
  ProposalRef,
  ProposalView,
  ProposalWithdrawInput,
  ProposeInput,
} from "@fm/v3/api";
import { Schema } from "effect";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  RecordNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

const errors = Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError);
const transitionErrors = Schema.Union(
  Unauthorized,
  Forbidden,
  RecordNotFoundError,
  ValidationError,
  DatabaseError,
  InvalidStateTransition,
);

/**
 * @since 1.0.0
 */
export class V3IntakeRpcs extends RpcGroup.make(
  /** A new proposal, or the next revision of one at the revision its person read. */
  Rpc.make("v3.intake.propose", { payload: ProposeInput, success: ProposalView, error: errors })
    .middleware(LoopActorMiddleware),
  Rpc.make("v3.intake.get", { payload: ProposalRef, success: ProposalView, error: errors })
    .middleware(LoopActorMiddleware),
  /** Its person's explicit approval of one exact revision and its material. */
  Rpc.make("v3.intake.approve", { payload: ProposalApproveInput, success: ProposalView, error: errors })
    .middleware(LoopActorMiddleware),
  Rpc.make("v3.intake.withdraw", { payload: ProposalWithdrawInput, success: ProposalView, error: errors })
    .middleware(LoopActorMiddleware),
  /** Starts a run of the loop from the exact revision its person was shown (and approved, when it needs it). */
  Rpc.make("v3.intake.start", { payload: IntakeStartInput, success: RunStartResult, error: transitionErrors })
    .middleware(LoopActorMiddleware),
) {}
